import React, { useEffect, useState, useCallback, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import Box from '@mui/joy/Box/index.js'
import Sheet from '@mui/joy/Sheet/index.js'
import Stack from '@mui/joy/Stack/index.js'
import Typography from '@mui/joy/Typography/index.js'
import Alert from '@mui/joy/Alert/index.js'
import IconButton from '@mui/joy/IconButton/index.js'
import InfoIcon from '@mui/icons-material/Info'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import ChatBubble from './ChatBubble.jsx'
import CitationBubble from './CitationBubble.jsx'
import MessageInput from './MessageInput.jsx'
import MessagesPaneHeader from './MessagesPaneHeader.jsx'
import { fetchAuthSession } from 'aws-amplify/auth'
import { w3cwebsocket as W3CWebSocket } from 'websocket'
import { put } from 'aws-amplify/api'

const host =
  window.location.host.includes('localhost') ?
    'tech-assistant-socket-v2.tr-dev.net'
  : window.location.host.replace('tech-assistant', 'tech-assistant-socket-v2')

export default function MessagesPane({
  conversation,
  isInputDisabled,
  showCitations = true,
  admin,
  view,
  onToggleView,
}) {
  console.log('Show citations:', showCitations)
  const [chatMessages, setChatMessages] = useState([
    {
      sender: 'Tech Assistant',
      content: 'Hello! How can I help you?',
    },
  ])
  const [textAreaValue, setTextAreaValue] = useState('')
  const [sessionId, setSessionId] = useState(uuidv4())
  const [cite, setCite] = useState({})
  const [conversationRating, setConversationRating] = useState(null)
  const wsClient = useRef(null)

  const toggleCite = (messageIndex) => {
    const currentCite =
      cite[messageIndex] !== undefined ? cite[messageIndex] : true
    const newCite = { ...cite, [messageIndex]: !currentCite }
    setCite(newCite)
  }

  useEffect(() => {
    void (async () => {
      const url = `wss://${host}/`
      const session = await fetchAuthSession()
      const idToken = session.tokens.idToken.toString()

      // For V2 API, pass the ID token as a query parameter
      const signedUrl = `${url}?idToken=${encodeURIComponent(idToken)}`

      console.log('Connecting to WebSocket V2 with Cognito auth')
      console.log('WebSocket host:', host)

      const client = new W3CWebSocket(signedUrl)
      // eslint-disable-next-line functional/immutable-data -- ws api
      client.onopen = async () => {
        console.log('WebSocket Client Connected')
      }
      // eslint-disable-next-line functional/immutable-data -- ws api
      client.onmessage = (message) => {
        const parsed = JSON.parse(message.data)

        // Log response block IDs for debugging
        if (parsed.responseBlockId) {
          console.log(
            `Received event ${parsed.event} with responseBlockId:`,
            parsed.responseBlockId,
          )
        }

        // Handle citations loading indicator
        if (parsed.event === 'citations-loading') {
          setChatMessages((messages) =>
            messages.map((msg) =>
              msg.responseBlockId === parsed.responseBlockId ?
                {
                  ...msg,
                  searching: true,
                  searchingText: 'Finding supporting documentation...',
                  streamingCitations: [],
                  citationsComplete: false,
                }
              : msg,
            ),
          )
          return
        }

        // Handle streaming citation results
        if (parsed.event === 'citation-result') {
          setChatMessages((messages) =>
            messages.map((msg) =>
              msg.responseBlockId === parsed.responseBlockId ?
                {
                  ...msg,
                  streamingCitations: [
                    ...(msg.streamingCitations || []),
                    parsed.citation,
                  ],
                }
              : msg,
            ),
          )
          return
        }

        // Handle citations complete
        if (parsed.event === 'citations-done') {
          setChatMessages((messages) =>
            messages.map((msg) =>
              msg.responseBlockId === parsed.responseBlockId ?
                {
                  ...msg,
                  context: msg.streamingCitations || [],
                  searching: false,
                  searchingText: undefined,
                  citationsComplete: true,
                  streamingCitations: undefined,
                }
              : msg,
            ),
          )
          return
        }

        // Handle final citations (backward compatibility)
        if (parsed.event === 'citations') {
          setChatMessages((messages) =>
            messages.map((msg) =>
              msg.responseBlockId === parsed.responseBlockId ?
                {
                  ...msg,
                  context: parsed.citations,
                  searching: false,
                  searchingText: undefined,
                }
              : msg,
            ),
          )
          return
        }

        // Handle remaining messages
        if (parsed.event === 'remaining-message-count') {
          console.log(`Remaining messages: ${parsed.nbrRemainingMessages}`)
          return
        }

        // Handle end of response
        if (parsed.event === 'end-response') {
          setChatMessages((messages) =>
            messages.map((msg, index) =>
              index === messages.length - 1 ?
                {
                  ...msg,
                  complete: true,
                  responseBlockId: parsed.responseBlockId,
                }
              : msg,
            ),
          )
          return
        }

        if (parsed.event !== 'partial-response') {
          return
        }
        setChatMessages((messages) =>
          messages.map((msg, index) => {
            // If the message already has a responseBlockId, match by that
            if (msg.responseBlockId === parsed.responseBlockId) {
              return {
                ...msg,
                content: msg.content + parsed.completion,
                searching: false,
              }
            }
            // Otherwise, if it's the last message and doesn't have a responseBlockId yet, assign it
            if (
              index === messages.length - 1 &&
              !msg.responseBlockId &&
              msg.sender === 'Tech Assistant'
            ) {
              return {
                ...msg,
                content: msg.content + parsed.completion,
                searching: false,
                responseBlockId: parsed.responseBlockId,
              }
            }
            return msg
          }),
        )
      }
      wsClient.current = client
    })()
  }, [])

  useEffect(() => {
    if (conversation) {
      setSessionId(conversation.conversationId)
      setConversationRating(conversation.rating ?? null)
      setChatMessages(
        conversation.messages
          .sort((a, b) => a.date - b.date)
          .map((message) => ({
            sender: message.author === 'bot' ? 'Tech Assistant' : 'You',
            content: message.message,
            complete: true,
            context: message.context,
            responseBlockId: message.responseBlockId,
          })),
      )
    }
  }, [conversation])

  const rateConversation = useCallback(
    async (rating) => {
      try {
        const session = await fetchAuthSession()
        const idToken = session.tokens.idToken.toString()

        await put({
          apiName: 'technician-bot',
          path: `/1/conversations/${sessionId}`,
          options: {
            headers: {
              Authorization: idToken,
            },
            body: {
              rating,
            },
          },
        }).response

        setConversationRating(rating)
      } catch (error) {
        console.error('Error rating conversation:', error)
      }
    },
    [sessionId],
  )

  const sendMessage = useCallback(async () => {
    // Save the message immediately
    const userMessage = textAreaValue
    setTextAreaValue('') // Clear input right away

    const session = await fetchAuthSession()
    const idToken = session.tokens.idToken.toString()

    setChatMessages((m) => [
      ...m,
      {
        sender: 'You',
        content: userMessage,
      },
      {
        sender: 'Tech Assistant',
        content: '',
        complete: false,
        searching: true,
      },
    ])

    try {
      wsClient.current.send(
        JSON.stringify({
          source: 'web',
          sessionId,
          message: userMessage,
          idToken,
          type: 'authentication',
        }),
      )
    } catch (error) {
      console.error('Error:', error)
      setChatMessages((messages) => [
        ...messages.slice(0, -1),
        {
          sender: 'Tech Assistant',
          content: '[error: ' + error.message + ']',
          complete: true,
        },
      ])
    }
  }, [textAreaValue, sessionId])

  return (
    <Sheet
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.level1',
      }}
    >
      <MessagesPaneHeader
        admin={admin}
        view={view}
        onToggleView={onToggleView}
      />
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          px: 2,
          py: 3,
          overflowY: 'scroll',
          flexDirection: 'column-reverse',
        }}
      >
        <Stack spacing={2} justifyContent="flex-end">
          {chatMessages.map((message, messageIndex) => {
            const isYou = message.sender === 'You'
            const showCite =
              cite[messageIndex] !== undefined ? cite[messageIndex] : true
            return (
              <div key={messageIndex} className="message">
                <Stack direction="column" spacing={2}>
                  <Stack
                    direction="row"
                    spacing={2}
                    flexDirection={isYou ? 'row-reverse' : 'row'}
                  >
                    <ChatBubble
                      variant={
                        isYou ? 'sent'
                        : message.complete ?
                          'received'
                        : 'incomplete'
                      }
                      hasCitation={
                        showCitations &&
                        (message.context || message.streamingCitations)
                      }
                      showCite={showCite}
                      toggleCite={() => {
                        toggleCite(messageIndex)
                      }}
                      {...message}
                    />
                  </Stack>
                  {showCitations &&
                    showCite &&
                    (message.context || message.streamingCitations) && (
                      <>
                        {(() => {
                          // Group citations by ObjectId
                          const groupedCitations = new Map()

                          // Process streaming citations
                          if (message.streamingCitations) {
                            message.streamingCitations.forEach((ctx) => {
                              if (ctx?.retrievedReferences?.[0]) {
                                ctx.retrievedReferences.forEach((retRef) => {
                                  const objectId =
                                    retRef.metadata?.ObjectId ||
                                    retRef.metadata?.[
                                      'x-amz-bedrock-kb-source-uri'
                                    ] ||
                                    'unknown'

                                  if (!groupedCitations.has(objectId)) {
                                    groupedCitations.set(objectId, {
                                      metadata: retRef.metadata,
                                      contents: [],
                                    })
                                  }

                                  groupedCitations
                                    .get(objectId)
                                    .contents.push(retRef.content)
                                })
                              }
                            })
                          }

                          // Process final context (backward compatibility)
                          if (
                            message.context &&
                            !message.streamingCitations &&
                            Array.isArray(message.context)
                          ) {
                            message.context.forEach((ctx) => {
                              if (ctx?.retrievedReferences?.[0]) {
                                ctx.retrievedReferences.forEach((retRef) => {
                                  const objectId =
                                    retRef.metadata?.ObjectId ||
                                    retRef.metadata?.[
                                      'x-amz-bedrock-kb-source-uri'
                                    ] ||
                                    'unknown'

                                  if (!groupedCitations.has(objectId)) {
                                    groupedCitations.set(objectId, {
                                      metadata: retRef.metadata,
                                      contents: [],
                                    })
                                  }

                                  groupedCitations
                                    .get(objectId)
                                    .contents.push(retRef.content)
                                })
                              }
                            })
                          }

                          // Render grouped citations
                          return Array.from(groupedCitations.entries()).map(
                            ([objectId, citation], index) => {
                              const cacheTitle =
                                citation.metadata?.CacheTitle || 'No title'
                              const referenceKey = `${messageIndex}-grouped-${objectId}-${index}`

                              return (
                                <CitationBubble
                                  key={referenceKey}
                                  referenceKey={referenceKey}
                                  isYou={isYou}
                                  cacheTitle={cacheTitle}
                                  metadata={citation.metadata}
                                  contextObj={citation.contents[0]} // For backward compatibility
                                  contents={citation.contents} // New prop for multiple contents
                                />
                              )
                            },
                          )
                        })()}
                      </>
                    )}
                </Stack>
              </div>
            )
          })}
        </Stack>
      </Box>

      {isInputDisabled ?
        <Box sx={{ px: 2, pb: 3 }}>
          <Alert
            startDecorator={<InfoIcon />}
            color="neutral"
            size="sm"
            variant="soft"
            sx={{ mb: 2 }}
          >
            <Typography level="body-sm">
              You are viewing another user&apos;s conversation. Input is
              disabled to prevent continuing their conversation.
            </Typography>
          </Alert>
          <MessageInput
            textAreaValue={textAreaValue}
            setTextAreaValue={setTextAreaValue}
            onSubmit={sendMessage}
            disabled={true}
          />
        </Box>
      : <Box sx={{ px: 2, pb: 3 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="center"
            sx={{ mb: 2 }}
          >
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Was this conversation helpful?
            </Typography>
            <IconButton
              variant={conversationRating === true ? 'solid' : 'outlined'}
              color={conversationRating === true ? 'primary' : 'neutral'}
              size="sm"
              onClick={() => {
                void rateConversation(true)
              }}
            >
              <ThumbUpIcon />
            </IconButton>
            <IconButton
              variant={conversationRating === false ? 'solid' : 'outlined'}
              color={conversationRating === false ? 'danger' : 'neutral'}
              size="sm"
              onClick={() => {
                void rateConversation(false)
              }}
            >
              <ThumbDownIcon />
            </IconButton>
          </Stack>
          <MessageInput
            textAreaValue={textAreaValue}
            setTextAreaValue={setTextAreaValue}
            onSubmit={sendMessage}
            disabled={false}
          />
        </Box>
      }
    </Sheet>
  )
}
